<template>
  <div v-if="visible" class="fixed inset-0 z-50 bg-white flex flex-col">
      <!-- 模态框内容 - 真正全屏 -->
      <div class="flex-1 flex flex-col h-full overflow-hidden p-6">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4 pb-4 border-b border-gray-200 flex-shrink-0">
          <div>
            <h3 class="text-xl font-semibold text-gray-900">
              邮件发送日志
            </h3>
          <div v-if="campaignInfo" class="mt-2 flex items-center gap-4 flex-wrap">
            <p class="text-sm text-gray-700">
              <span class="font-medium">活动名称：</span>{{ campaignInfo.name }}
            </p>
            <p class="text-xs text-gray-500">
              <span class="font-medium">UUID：</span>{{ campaignInfo.id }}
            </p>
          </div>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2 rounded-full transition-all"
            title="关闭"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 统计摘要 -->
        <div v-if="summary" class="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-4 flex-shrink-0">
          <div class="bg-blue-50 rounded-lg p-3 text-center">
            <div class="text-lg font-semibold text-blue-600">{{ summary.total }}</div>
            <div class="text-xs text-blue-700">总数</div>
          </div>
          <div class="bg-yellow-50 rounded-lg p-3 text-center">
            <div class="text-lg font-semibold text-yellow-600">{{ summary.pending }}</div>
            <div class="text-xs text-yellow-700">待发送</div>
          </div>
          <div class="bg-green-50 rounded-lg p-3 text-center">
            <div class="text-lg font-semibold text-green-600">{{ summary.sent }}</div>
            <div class="text-xs text-green-700">已发送</div>
          </div>
          <div class="bg-purple-50 rounded-lg p-3 text-center">
            <div class="text-lg font-semibold text-purple-600">{{ summary.opened }}</div>
            <div class="text-xs text-purple-700">已打开</div>
          </div>
          <div class="bg-red-50 rounded-lg p-3 text-center">
            <div class="text-lg font-semibold text-red-600">{{ summary.failed }}</div>
            <div class="text-xs text-red-700">发送失败</div>
          </div>
        </div>

        <!-- 过滤器 -->
        <div class="flex flex-wrap gap-3 mb-4 p-3 bg-gray-50 rounded-lg flex-shrink-0">
          <div class="flex-1 min-w-40">
            <label class="block text-xs font-medium text-gray-700 mb-1">状态过滤</label>
            <select
              v-model="filters.status"
              @change="applyFilters"
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">全部状态</option>
              <option value="pending">待发送</option>
              <option value="sent">已发送</option>
              <option value="opened">已打开</option>
              <option value="failed">发送失败</option>
            </select>
          </div>
          <div class="flex-1 min-w-40">
            <label class="block text-xs font-medium text-gray-700 mb-1">邮箱搜索</label>
            <input
              v-model="filters.email"
              @input="debounceSearch"
              type="text"
              placeholder="搜索邮箱地址..."
              class="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <!-- 日志列表 -->
        <div class="bg-white border border-gray-200 rounded-lg overflow-hidden flex-1 flex flex-col min-h-0">
          <!-- 表头 -->
          <div class="bg-gray-50 px-4 py-2 border-b border-gray-200 flex-shrink-0">
            <div class="grid grid-cols-11 gap-2 text-xs font-medium text-gray-700">
              <div class="col-span-2">收件人</div>
              <div class="col-span-2">发送账户</div>
              <div class="col-span-1">状态</div>
              <div class="col-span-2">发送时间</div>
              <div class="col-span-2">最近回复</div>
              <div class="col-span-2">操作</div>
            </div>
          </div>

          <!-- 加载状态 -->
          <div v-if="loading" class="flex justify-center items-center py-8 flex-1">
            <svg class="animate-spin h-6 w-6 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>

          <!-- 日志条目 -->
          <div v-else-if="logs.length > 0" class="divide-y divide-gray-200 overflow-y-auto flex-1">
            <div
              v-for="log in logs"
              :key="log.id"
              class="px-4 py-3 hover:bg-gray-50"
            >
              <div class="grid grid-cols-11 gap-2 items-center text-xs">
                <div class="col-span-2">
                  <div class="font-medium text-gray-900 text-xs truncate" :title="log.to_email">
                    {{ log.to_email }}
                  </div>
                </div>
                <div class="col-span-2">
                  <div class="text-blue-600 text-xs truncate" :title="log.from_email">
                    {{ log.from_email }}
                  </div>
                </div>
                <div class="col-span-1">
                  <span :class="getStatusBadgeClass(log.status)" class="inline-flex px-1 py-0.5 text-xs font-medium rounded-full">
                    {{ getStatusText(log.status) }}
                  </span>
                </div>
                <div class="col-span-2 text-gray-600 text-xs">
                  {{ log.sent_at ? formatDateTime(log.sent_at) : '-' }}
                </div>
                <div class="col-span-2 text-gray-600 text-xs">
                  <div v-if="log.latest_reply_time" class="flex flex-col">
                    <span class="font-medium text-green-600">{{ formatDateTime(log.latest_reply_time) }}</span>
                    <!--<span class="text-xs text-gray-500">({{ log.reply_count || 0 }}次回复)</span>-->
                  </div>
                  <span v-else class="text-gray-400">未回复</span>
                </div>
                <div class="col-span-2">
                  <div class="flex items-center gap-1">
                    <button
                      @click="viewEmailContent(log)"
                      class="inline-flex items-center text-blue-600 hover:text-blue-900 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded text-xs transition-colors whitespace-nowrap"
                      title="查看邮件内容"
                    >
                      <EyeIcon class="h-3.5 w-3.5 mr-1" />
                      内容
                    </button>
                    <button
                      @click="viewCustomer(log)"
                      :disabled="!log.customer_id || loadingCustomers.has(log.id)"
                      class="inline-flex items-center text-green-600 hover:text-green-900 bg-green-50 hover:bg-green-100 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      title="查看客户信息"
                    >
                      <UserIcon v-if="!loadingCustomers.has(log.id)" class="h-3.5 w-3.5 mr-1" />
                      <svg v-else class="animate-spin h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      客户
                    </button>
                    <button
                      v-if="log.has_automation"
                      @click="viewAutomationFlow(log)"
                      :disabled="loadingAutomationFlows.has(log.id)"
                      class="inline-flex items-center text-purple-600 hover:text-purple-900 bg-purple-50 hover:bg-purple-100 px-2 py-1 rounded text-xs transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                      title="查看自动化流程"
                    >
                      <svg v-if="!loadingAutomationFlows.has(log.id)" class="h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                      </svg>
                      <svg v-else class="animate-spin h-3.5 w-3.5 mr-1" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      流程
                    </button>
                  </div>
                </div>
              </div>
              <!-- 错误信息 -->
              <div v-if="log.error_message" class="mt-2">
                <div class="flex items-start gap-2 text-xs text-red-600 bg-red-50 p-2 rounded">
                  <div class="flex-1 min-w-0">
                    <div 
                      :class="expandedErrors.has(log.id) ? '' : 'line-clamp-1'"
                      class="whitespace-pre-wrap break-words font-mono text-xs"
                    >
                      {{ log.error_message }}
                    </div>
                  </div>
                  <button
                    @click="toggleErrorExpand(log.id)"
                    class="flex-shrink-0 text-red-700 hover:text-red-900 bg-red-100 hover:bg-red-200 px-2 py-1 rounded transition-colors font-medium whitespace-nowrap"
                  >
                    {{ expandedErrors.has(log.id) ? '收起' : '查看详细' }}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- 无数据状态 -->
          <div v-else class="flex-1 flex items-center justify-center">
            <div class="text-center">
              <DocumentTextIcon class="mx-auto h-12 w-12 text-gray-400" />
              <h3 class="mt-4 text-base font-medium text-gray-900">暂无日志数据</h3>
              <p class="mt-2 text-sm text-gray-500">
                {{ filters.status || filters.email ? '没有找到匹配的日志记录' : '该活动还未开始发送邮件' }}
              </p>
            </div>
          </div>
        </div>

        <!-- 分页 -->
        <div v-if="pagination && pagination.total > 0" class="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 flex-shrink-0">
          <div class="text-xs text-gray-700">
            显示第 {{ (pagination.current_page - 1) * pagination.per_page + 1 }} - 
            {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }} 条，
            共 {{ pagination.total }} 条记录
          </div>
          <div class="flex space-x-2">
            <button
              @click="goToPage(pagination.current_page - 1)"
              :disabled="pagination.current_page <= 1"
              class="px-2 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              上一页
            </button>
            <button
              @click="goToPage(pagination.current_page + 1)"
              :disabled="pagination.current_page >= pagination.total_pages"
              class="px-2 py-1 text-xs border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              下一页
            </button>
          </div>
        </div>
      </div>
  </div>
  
  <!-- 邮件内容预览模态框 -->
  <div v-if="showEmailPreviewModal" class="fixed inset-0 z-[9999] overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity" @click="showEmailPreviewModal = false">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">邮件内容预览</h3>
          <button @click="showEmailPreviewModal = false" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>
        <div v-if="selectedEmailLog" class="border rounded-lg p-6">
          <div class="mb-4">
            <div class="text-sm text-gray-500">收件人:</div>
            <div class="font-medium">{{ selectedEmailLog.to_email }}</div>
          </div>
          <div class="mb-4">
            <div class="text-sm text-gray-500">邮件主题:</div>
            <div class="font-medium">{{ selectedEmailLog.email_subject || '无主题' }}</div>
          </div>
          <div class="border-t pt-4">
            <div class="text-sm text-gray-500 mb-2">邮件内容:</div>
            <div class="prose max-w-none" v-html="selectedEmailLog.email_content || '暂无内容'"></div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 客户详情预览模态框 -->
  <div v-if="showCustomerPreviewModal" class="fixed inset-0 z-[9999] overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity" @click="showCustomerPreviewModal = false">
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-5xl sm:w-full sm:p-6">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">客户详情</h3>
          <button @click="showCustomerPreviewModal = false" class="text-gray-400 hover:text-gray-600">
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>
        <div v-if="selectedCustomer" class="border rounded-lg p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
            <!-- 基本信息 -->
            <div class="space-y-4">
              <h4 class="text-md font-medium text-gray-900 mb-3">基本信息</h4>
              <div>
                <div class="text-sm text-gray-500">姓名</div>
                <div class="font-medium">{{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }}</div>
              </div>
              <div>
                <div class="text-sm text-gray-500">邮箱</div>
                <div class="font-medium">{{ selectedCustomer.email || '无' }}</div>
              </div>
              <div>
                <div class="text-sm text-gray-500">电话</div>
                <div class="font-medium">{{ selectedCustomer.phone || '无' }}</div>
              </div>
              <div>
                <div class="text-sm text-gray-500">来源</div>
                <div class="font-medium">{{ selectedCustomer.source || '无' }}</div>
              </div>
              <div v-if="selectedCustomer.order_number">
                <div class="text-sm text-gray-500">订单号</div>
                <div class="font-medium">{{ selectedCustomer.order_number }}</div>
              </div>
            </div>

            <!-- 其他信息 -->
            <div class="space-y-4">
              <h4 class="text-md font-medium text-gray-900 mb-3">其他信息</h4>
              <div v-if="selectedCustomer.address">
                <div class="text-sm text-gray-500">地址</div>
                <div class="font-medium">{{ selectedCustomer.address }}</div>
              </div>
              <div v-if="selectedCustomer.notes">
                <div class="text-sm text-gray-500">备注</div>
                <div class="font-medium">{{ selectedCustomer.notes }}</div>
              </div>
              <div>
                <div class="text-sm text-gray-500">创建时间</div>
                <div class="font-medium">{{ formatDateTime(selectedCustomer.created_at) }}</div>
              </div>
            </div>
          </div>

          <!-- 标签 -->
          <div v-if="selectedCustomer.tags && selectedCustomer.tags.length > 0" class="mt-6 pt-6 border-t">
            <div class="text-sm text-gray-500 mb-2">标签</div>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="tag in selectedCustomer.tags"
                :key="tag.id"
                :style="{ 
                  backgroundColor: tag.background_color || '#3B82F6', 
                  color: tag.text_color || '#FFFFFF' 
                }"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
              >
                {{ tag.name }}
              </span>
            </div>
          </div>

          <!-- 分组 -->
          <div v-if="selectedCustomer.groups && selectedCustomer.groups.length > 0" class="mt-4">
            <div class="text-sm text-gray-500 mb-2">分组</div>
            <div class="flex flex-wrap gap-2">
              <span
                v-for="group in selectedCustomer.groups"
                :key="group.id"
                :style="{ backgroundColor: (group.color || '#3B82F6') + '20', color: group.color || '#3B82F6' }"
                class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
              >
                {{ group.name }}
              </span>
            </div>
          </div>

          <!-- 自定义字段 -->
          <div v-if="selectedCustomer.custom_fields && selectedCustomer.custom_fields.length > 0" class="mt-6 pt-6 border-t">
            <div class="text-sm text-gray-500 mb-3">自定义字段</div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                v-for="field in selectedCustomer.custom_fields"
                :key="field.field_id"
                class="space-y-1"
              >
                <div class="text-sm text-gray-500">{{ field.label }}</div>
                <div class="font-medium">{{ field.value || '无' }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- 自动化流程预览模态框 -->
  <div 
    v-if="showAutomationFlowModal" 
    class="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
    @click.self="showAutomationFlowModal = false"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-hidden">
      <!-- 模态框标题 -->
      <div class="flex items-center justify-between p-6 border-b">
        <h3 class="text-lg font-semibold text-gray-900">自动化跟进流程</h3>
        <button 
          @click="showAutomationFlowModal = false"
          class="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <XMarkIcon class="h-6 w-6" />
        </button>
      </div>
      
      <!-- 模态框内容 -->
      <div class="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
        <div v-if="loadingAutomationFlow" class="flex items-center justify-center py-8">
          <svg class="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 818-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        
        <div v-else-if="selectedAutomationFlow">
          <!-- 原始邮件信息 -->
          <div class="bg-blue-50 rounded-lg p-4 mb-6">
            <h4 class="font-medium text-blue-900 mb-2">原始邮件</h4>
            <div class="space-y-2 text-sm">
              <div><span class="text-blue-600">主题:</span> {{ selectedAutomationFlow.original_email.subject }}</div>
              <div><span class="text-blue-600">收件人:</span> {{ selectedAutomationFlow.original_email.recipient }}</div>
              <div><span class="text-blue-600">发送时间:</span> {{ formatDateTime(selectedAutomationFlow.original_email.sent_at) }}</div>
              <div><span class="text-blue-600">状态:</span> 
                <span :class="getStatusBadgeClass(selectedAutomationFlow.original_email.status)" class="px-2 py-1 rounded-full text-xs">
                  {{ getStatusText(selectedAutomationFlow.original_email.status) }}
                </span>
              </div>
            </div>
          </div>

          <!-- 自动化步骤时间线 -->
          <div v-if="selectedAutomationFlow.automation_steps && selectedAutomationFlow.automation_steps.length > 0">
            <h4 class="font-medium text-gray-900 mb-4">自动化跟进时间线</h4>
            <div class="space-y-4">
              <div 
                v-for="(step, index) in selectedAutomationFlow.automation_steps" 
                :key="step.id"
                class="flex items-start"
              >
                <!-- 时间线左侧 -->
                <div class="flex flex-col items-center mr-4">
                  <div :class="getAutomationStepIconClass(step.status)" class="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium">
                    {{ index + 1 }}
                  </div>
                  <div v-if="index < selectedAutomationFlow.automation_steps.length - 1" class="w-px h-16 bg-gray-200 mt-2"></div>
                </div>
                
                <!-- 步骤内容 -->
                <div class="flex-1 min-w-0">
                  <div class="bg-gray-50 rounded-lg p-4">
                    <div class="flex items-center justify-between mb-2">
                      <h5 class="font-medium text-gray-900">{{ step.step_display }}</h5>
                      <span :class="getAutomationStatusBadgeClass(step.status)" class="px-2 py-1 rounded-full text-xs">
                        {{ step.status_display }}
                      </span>
                    </div>
                    
                    <div class="text-sm text-gray-600 space-y-1">
                      <div><span class="font-medium">触发条件:</span> {{ step.trigger_display }}</div>
                      <div><span class="font-medium">计划时间:</span> {{ formatDateTime(step.scheduled_at) }}</div>
                      <div v-if="step.sent_at"><span class="font-medium">实际发送:</span> {{ formatDateTime(step.sent_at) }}</div>
                    </div>
                    
                    <!-- 执行详情 -->
                    <div v-if="step.execution" class="mt-3 p-3 bg-white rounded border">
                      <h6 class="font-medium text-gray-900 mb-1">执行详情</h6>
                      <div class="text-sm text-gray-600 space-y-1">
                        <div><span class="font-medium">邮件主题:</span> {{ step.execution.subject }}</div>
                        <div><span class="font-medium">发送时间:</span> {{ formatDateTime(step.execution.sent_at) }}</div>
                        <div><span class="font-medium">状态:</span> 
                          <span :class="getStatusBadgeClass(step.execution.status)" class="px-2 py-1 rounded-full text-xs">
                            {{ getStatusText(step.execution.status) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div v-else class="text-center py-8 text-gray-500">
            暂无自动化跟进计划
          </div>
        </div>
      </div>
      
      <!-- 模态框底部 -->
      <div class="flex justify-end p-6 border-t">
        <button 
          @click="showAutomationFlowModal = false"
          class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
        >
          关闭
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, computed, onMounted } from 'vue'
import { 
  XMarkIcon, 
  DocumentTextIcon,
  EyeIcon,
  UserIcon
} from '@heroicons/vue/24/outline'
import campaignService from '@/services/campaignService'
import { customerService } from '@/services/customerService'

const props = defineProps({
  visible: {
    type: Boolean,
    default: false
  },
  campaign: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['close'])

// 响应式数据
const loading = ref(false)
const logs = ref([])
const summary = ref(null)
const campaignInfo = ref(null)
const pagination = ref(null)
const filters = ref({
  status: '',
  email: ''
})

// 邮件内容预览相关状态
const showEmailPreviewModal = ref(false)
const selectedEmailLog = ref(null)

// 客户详情预览相关状态
const showCustomerPreviewModal = ref(false)
const selectedCustomer = ref(null)
const loadingCustomers = ref(new Set())

// 自动化流程预览相关状态
const showAutomationFlowModal = ref(false)
const selectedAutomationFlow = ref(null)
const loadingAutomationFlow = ref(false)
const loadingAutomationFlows = ref(new Set())

// 错误信息展开状态
const expandedErrors = ref(new Set())

let searchTimeout = null

// 切换错误信息展开状态
const toggleErrorExpand = (logId) => {
  if (expandedErrors.value.has(logId)) {
    expandedErrors.value.delete(logId)
  } else {
    expandedErrors.value.add(logId)
  }
}

// 关闭模态框
const closeModal = () => {
  // 清理加载状态
  loadingCustomers.value.clear()
  showCustomerPreviewModal.value = false
  selectedCustomer.value = null
  showAutomationFlowModal.value = false
  selectedAutomationFlow.value = null
  loadingAutomationFlow.value = false
  loadingAutomationFlows.value.clear()
  expandedErrors.value.clear()
  emit('close')
}

// 获取日志数据
const loadLogs = async (page = 1) => {
  if (!props.campaign) return
  
  loading.value = true
  try {
    const result = await campaignService.getCampaignLogs(props.campaign.id, {
      page,
      per_page: 20,
      status: filters.value.status || undefined,
      email: filters.value.email || undefined
    })
    
    if (result.success) {
      // API返回的数据结构：{count, next, previous, results: [...], statistics: {...}, campaign_info: {...}}
      logs.value = result.data.results || []
      
      // 使用后端返回的活动信息
      if (result.data.campaign_info) {
        campaignInfo.value = result.data.campaign_info
      }
      
      // 使用后端返回的统计数据（该活动的全部日志统计，而不是当前页的）
      if (result.data.statistics) {
        summary.value = {
          total: result.data.statistics.total || 0,
          pending: result.data.statistics.pending || 0,
          sent: result.data.statistics.sent || 0,
          opened: result.data.statistics.opened || 0,
          failed: result.data.statistics.failed || 0
        }
      } else {
        // 降级处理：如果后端没有返回统计数据，则使用count字段
        const totalCount = result.data.count || 0
        summary.value = {
          total: totalCount,
          pending: 0,
          sent: 0,
          opened: 0,
          failed: 0
        }
      }
      
      // 构建分页信息
      const pageSize = 20
      const currentPage = page || 1 // 使用传入的页码参数
      const totalCount = result.data.count || 0
      const totalPages = Math.ceil(totalCount / pageSize)
      
      pagination.value = {
        total: totalCount,
        per_page: pageSize,
        current_page: currentPage,
        total_pages: totalPages,
        has_next: !!result.data.next,
        has_prev: !!result.data.previous
      }
    } else {
      console.error('获取日志失败:', result.error)
      logs.value = []
    }
  } catch (error) {
    console.error('获取日志失败:', error)
    logs.value = []
  } finally {
    loading.value = false
  }
}

// 应用过滤器
const applyFilters = () => {
  loadLogs(1)
}

// 防抖搜索
const debounceSearch = () => {
  clearTimeout(searchTimeout)
  searchTimeout = setTimeout(() => {
    applyFilters()
  }, 500)
}

// 翻页
const goToPage = (page) => {
  loadLogs(page)
}

// 查看邮件内容
const viewEmailContent = (log) => {
  selectedEmailLog.value = log
  showEmailPreviewModal.value = true
}

// 查看客户详情
const viewCustomer = async (log) => {
  if (!log.customer_id) {
    console.error('日志中没有客户信息')
    return
  }

  // 将当前日志ID添加到加载状态集合
  loadingCustomers.value.add(log.id)
  
  try {
    const result = await customerService.getCustomer(log.customer_id)
    if (result.success) {
      selectedCustomer.value = result.data
      showCustomerPreviewModal.value = true
    } else {
      console.error('获取客户信息失败:', result.message)
    }
  } catch (error) {
    console.error('获取客户信息失败:', error)
  } finally {
    // 从加载状态集合中移除当前日志ID
    loadingCustomers.value.delete(log.id)
  }
}

// 查看自动化流程
const viewAutomationFlow = async (log) => {
  loadingAutomationFlows.value.add(log.id)
  loadingAutomationFlow.value = true
  
  try {
    const result = await campaignService.getAutomationFlow(log.id)
    if (result.success) {
      selectedAutomationFlow.value = result.data
      showAutomationFlowModal.value = true
    } else {
      console.error('获取自动化流程失败:', result.error)
    }
  } catch (error) {
    console.error('获取自动化流程失败:', error)
  } finally {
    loadingAutomationFlows.value.delete(log.id)
    loadingAutomationFlow.value = false
  }
}

// 获取状态徽章样式
const getStatusBadgeClass = (status) => {
  const classes = {
    'pending': 'bg-yellow-100 text-yellow-800',
    'sent': 'bg-green-100 text-green-800',
    'delivered': 'bg-green-100 text-green-800', // delivered也显示为已发送样式
    'opened': 'bg-purple-100 text-purple-800',
    'failed': 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

// 获取状态文本
const getStatusText = (status) => {
  const texts = {
    'pending': '待发送',
    'sent': '已发送',
    'delivered': '已发送', // delivered也显示为已发送
    'opened': '已打开',
    'failed': '发送失败'
  }
  return texts[status] || status
}

// 获取自动化步骤图标样式
const getAutomationStepIconClass = (status) => {
  const classes = {
    'scheduled': 'bg-yellow-500',
    'sent': 'bg-green-500',
    'cancelled': 'bg-gray-500',
    'failed': 'bg-red-500'
  }
  return classes[status] || 'bg-gray-500'
}

// 获取自动化状态徽章样式
const getAutomationStatusBadgeClass = (status) => {
  const classes = {
    'scheduled': 'bg-yellow-100 text-yellow-800',
    'sent': 'bg-green-100 text-green-800',
    'cancelled': 'bg-gray-100 text-gray-800',
    'failed': 'bg-red-100 text-red-800'
  }
  return classes[status] || 'bg-gray-100 text-gray-800'
}

// 格式化日期时间
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  })
}

// 监听模态框显示状态
watch(() => props.visible, (newValue) => {
  if (newValue && props.campaign) {
    loadLogs()
  }
})
</script>
